import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import getConfig from 'next/config';
import Router from 'next/router';
import jsCookie from 'js-cookie';

import { LeadFormCategoryProps } from './LeadFormInterface';
import { sendToHubspot, sendLeads, sendToZendesk } from '../../lib/leadformHandler';
import { HTTPClient } from '../../lib/api';

import '../../utils/adwords';
import utm from '../../utils/utm';
import { GTM } from '../../utils/gtm';
import { getLeadFormCookie, setLeadFormCookie } from '../../utils/cookies';

import styles from './LeadForm.module.scss';
import CallNotification from '../Widget/CallNotification/CallNotification';
import WALeadForm from './WALeadForm/WALeadForm';
import { autoFormRedirectHandler } from '../../lib/redirectHandler/AutoFormRedirectHandler';
import { healthFormRedirectHandler } from '../../lib/redirectHandler/HealthFormRedirectHandler';
import { lifeFormRedirectHandler } from '../../lib/redirectHandler/LifeFormRedirectHandler';
import { sendToSendgrid, sendToSendgridAuto, sendToSendgridHealth, sendToSendgridLife } from '../../lib/submitHandler/sendgrid';
import { sendToFacebookConversion } from '../../lib/submitHandler/facebookConversion';
import redirectWhatsapp from '../../lib/submitHandler/redirectWhatsapp';

const { CORE_URL, BASE_URL_MEDIA } = getConfig().publicRuntimeConfig;
const { FB_PIXEL_ID } = getConfig().serverRuntimeConfig;

interface LeadFormProps {
  height?: string;
  width?: string;
  category: string;
  category_value?: string;
  background?: 'dark' | 'light';
  shouldShowTitle?: boolean;
  gtmAction: string;
  buttonText?: string;
  submittedMediaLeadform?: Function;
  submittedClaimLeadform?: Function;
  isInarticleLeadform?: boolean;
  closeWAPopup?: () => void;
}

export const LeadForm = ({
  height,
  width,
  category,
  category_value,
  background,
  gtmAction,
  buttonText,
  submittedMediaLeadform,
  submittedClaimLeadform,
  isInarticleLeadform,
  closeWAPopup
}: LeadFormProps) => {
  const [isLoading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showCallNotification, setShowCallNotification] = useState(false);
  const buttonVariant = background === 'dark' ? 'secondary' : 'primary';
  const sendToLeadList = ['health', 'life', 'auto'];

  /**
   * LeadForms obj, which is responsible to hold all the categories boostrap
   */
  const getLeadForm = (category) => {
    const LeadFormConfig = {
      health: () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const Health = dynamic<LeadFormCategoryProps>(() => import('./Health/Health') as any);
            return (
              <Health
                hasFullName={false}
                category="health"
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                leadFormCookie={getLeadFormCookie()}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      health_v2: () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const HealthV2 = dynamic<LeadFormCategoryProps>(
              () => import('./HealthV2/HealthV2') as any
            );
            return (
              <HealthV2
                category="health"
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                leadFormCookie={getLeadFormCookie()}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      health_fullname: () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const Health = dynamic<LeadFormCategoryProps>(() => import('./Health/Health') as any);
            return (
              <Health
                hasFullName={true}
                category="health"
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                leadFormCookie={getLeadFormCookie()}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      life: () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const Health = dynamic<LeadFormCategoryProps>(() => import('./Health/Health') as any);
            return (
              <Health
                hasFullName={false}
                category="life"
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                leadFormCookie={getLeadFormCookie()}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      life_v2: () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const HealthV2 = dynamic<LeadFormCategoryProps>(
              () => import('./HealthV2/HealthV2') as any
            );
            return (
              <HealthV2
                category="health"
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                leadFormCookie={getLeadFormCookie()}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      life_fullname: () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const Health = dynamic<LeadFormCategoryProps>(() => import('./Health/Health') as any);
            return (
              <Health
                hasFullName={true}
                category="life"
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                leadFormCookie={getLeadFormCookie()}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      auto: () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const Auto = dynamic<LeadFormCategoryProps>(() => import('./Auto/Auto') as any);
            return (
              <Auto
                category={category}
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                leadFormCookie={getLeadFormCookie()}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      moto: () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const Moto = dynamic<LeadFormCategoryProps>(() => import('./Moto/Moto') as any);
            return (
              <Moto
                category={category}
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                leadFormCookie={getLeadFormCookie()}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      travel: () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const Unassisted = dynamic<LeadFormCategoryProps>(
              () => import('./Unassisted/Unassisted') as any
            );
            return (
              <Unassisted
                category={category}
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      syariah: () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const Unassisted = dynamic<LeadFormCategoryProps>(
              () => import('./Unassisted/Unassisted') as any
            );
            return (
              <Unassisted
                category={category}
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      'micro-rawat-inap': () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const Unassisted = dynamic<LeadFormCategoryProps>(
              () => import('./Unassisted/Unassisted') as any
            );
            return (
              <Unassisted
                category={category}
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      business: () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const Business = dynamic<LeadFormCategoryProps>(
              () => import('./Business/Business') as any
            );
            return (
              <Business
                category={category}
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                leadFormCookie={getLeadFormCookie()}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      media: () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const Media = dynamic<LeadFormCategoryProps>(() => import('./Media/Media') as any);
            return (
              <Media
                category={category}
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                leadFormCookie={getLeadFormCookie()}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      claim: () => {
        return {
          // eslint-disable-next-line react/display-name
          component: () => {
            const Claim = dynamic<LeadFormCategoryProps>(() => import('./Claim/Claim') as any);
            return (
              <Claim
                category="claim"
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                buttonVariant={buttonVariant}
                leadFormCookie={getLeadFormCookie()}
                buttonText={buttonText}
                isInarticleLeadform={isInarticleLeadform}
              />
            );
          },
        };
      },
      whatsapp: () => {
        return {
          component: () => {
            return (
              <WALeadForm
                category="whatsapp"
                loading={isLoading}
                submitHandler={leadFormHandler}
                errorMessage={errorMessage}
                closePopup={closeWAPopup}
                buttonText={buttonText}
              />
            );
          }
        }
      },
    };
    return LeadFormConfig[category];
  };

  const leadFormHandler = async (values, category) => {
    setLoading(true);
    let data;
    let queryParams = {};

    const name = values.fullName ? values.fullName.split(' ') : '';
    const firstName = name.length > 0 ? name.shift() : name;
    const lastName = name.length > 0 ? name.join(' ') : '';

    switch (category) {
      case 'health':
      case 'life':
        data = {
          category: category,
          gender: values.gender,
          age: values.age,
          email: values.email ? values.email : "",
          phoneNumber: values.phoneNumber.replace(/\s/g, ''),
          fullName: values.fullName,
          firstName: firstName,
          lastName: lastName,
        };
        break;
      case 'auto':
        data = {
          category: category,
          brandId: values.brandId,
          modelId: values.modelId,
          typeId: values.typeId,
          yearId: values.yearId,
          brand: values.brand,
          model: values.model,
          type: values.type,
          year: values.year,
          area: values.area,
          areaCode: values.areaCode,
          fullName: values.fullName,
          firstName: firstName,
          lastName: lastName,
          email: values.email,
          phoneNumber: values.phoneNumber.replace(/\s/g, ''),
          namePlan: `${values.brand}_${values.model}_${values.type}_${values.areaCode}`,
        };
        break;
      case 'moto':
        data = {
          category: category,
          brandId: values.brandId,
        };
        break;
      case 'travel':
      case 'syariah':
      case 'micro-rawat-inap':
        data = {
          category: category,
        };
        break;
      case 'business':
        data = {
          category: category,
          company: values.company,
          number_of_employees: values.employees,
          email: values.email,
          phoneNumber: values.phoneNumber.replace(/\s/g, ''),
        };
        break;
      case 'media':
        data = {
          category: values.insuranceType,
          fullName: values.fullName,
          firstName: firstName,
          lastName: lastName,
          phoneNumber: values.phoneNumber.replace(/\s/g, ''),
        };
        break;
      case 'claim':
        data = {
          category: values.insuranceCategory,
          insuranceBrand: values.insuranceBrand,
          fullName: values.fullName,
          phoneNumber: values.phoneNumber.replace(/\s/g, ''),
          subscription: values.subscription,
        };
        break;
      case 'whatsapp':
        data = {
          category: values.insuranceType,
          fullName: values.fullName,
        };
        break;
    }

    if (category === 'business') {
      // store data to the cookies
      setLeadFormCookie(data);

      const resHubSpot = await sendToHubspot(data);

      if (resHubSpot) {
        queryParams = new URLSearchParams({
          number_of_employee: data.number_of_employees,
        });

        setLoading(false);
        window.location.href = `https://app.lifepal.co.id/register?${queryParams.toString()}`;
      }
    } else if (sendToLeadList.includes(category)) {
      // store data to the cookies
      setLeadFormCookie(data);
      setShowCallNotification(true);
      const resLeads = await sendLeads(data, isInarticleLeadform);

      const lead_id = resLeads.id ? resLeads.id : '';
      jsCookie.set('lead_id_cookie', lead_id, { expires: 14 });
      if (resLeads.created_at) {
        GTM.sendGAEvent(resLeads, gtmAction);
        await sendToDgraph(data);
        await sendToFBpixel(data, lead_id);

        if (category === 'auto') {
          if (resLeads.new_lead) await sendToFacebookConversion(lead_id, data);
          await sendToComparisonEmail(data);
          await sendToSendgrid(data);
          await sendToSendgridAuto(lead_id, data);
          setLoading(false);
          autoFormRedirectHandler(data);
        } else if (category === 'health') {
          if (data.email) await sendToSendgrid(data);
          await sendToSendgridHealth(lead_id, data);
          setLoading(false);
          healthFormRedirectHandler(data);
        } else if (category === 'life') {
          if (data.email) await sendToSendgrid(data);
          await sendToSendgridLife(lead_id, data);
          setLoading(false);
          lifeFormRedirectHandler(data);
        }

      } else {
        alert('Terjadi kesalahan pada sistem, silahkan coba beberapa saat lagi');
        setLoading(false);
        return '';
      }
    } else if (category === 'media') {
      // store data to the cookies
      setLeadFormCookie(data);
      const resLeads = await sendLeads(data, isInarticleLeadform);

      if (resLeads) {
        GTM.sendGAEvent(resLeads, gtmAction);
        setLoading(false);
        submittedMediaLeadform();
      } else {
        alert('Terjadi kesalahan pada sistem, silahkan coba beberapa saat lagi');
        setLoading(false);
        return '';
      }
    } else if (category === 'claim') {
      setLeadFormCookie(data);
      const response = await sendToZendesk(data);

      if (response && response.ticket) {
        GTM.sendClaimGAEvent(data, gtmAction);
        setLoading(false);
        submittedClaimLeadform();
      } else {
        alert('Terjadi kesalahan pada sistem, silahkan coba beberapa saat lagi');
        setLoading(false);
        return '';
      }
    } else if (category === 'whatsapp') {
      const resSendGrid = await sendToSendgrid(data);

      if (resSendGrid && resSendGrid.persisted_recipients.length > 0) {
        const sendgridPayload = {
          method: 'POST',
          body: JSON.stringify([{ id: resSendGrid.persisted_recipients[0].id }]),
        };
        await HTTPClient(`${BASE_URL_MEDIA}api/external/sendgrid-list/`, sendgridPayload);
      }
      redirectWhatsapp(data);
    } else {
      if (category) {
        queryParams = new URLSearchParams({
          category: category,
        });

        setLoading(false);
        window.location.href = `${CORE_URL}/search?${queryParams.toString()}`;
      } else {
        alert('Terjadi kesalahan pada sistem, silahkan coba beberapa saat lagi');
        setLoading(false);
        return '';
      }
    }

    if (global && global.LPGlobals) {
      if (data.email) {
        global.LPGlobals.setEmail(data.email);
      }

      if (data.phoneNumber) {
        global.LPGlobals.setPhoneNumber(data.phoneNumber);
      }
    }
  };

  const sendToComparisonEmail = (data) => {
    const firstName = data.firstName || '';
    const lastName = data.lastName || '';

    const orderQuery = new URLSearchParams({
      planId: '0',
      email: data.email,
      brand: data.brand,
      model: data.model,
      type: data.type,
      year: data.year,
      region: data.areaCode,
      fullName: `${firstName} ${lastName}`,
      phoneNumber: data.phoneNumber,
    });

    return HTTPClient(`${BASE_URL_MEDIA}api/orders/comparison/?${orderQuery.toString()}`, {});
  };

  const sendToDgraph = (data) => {
    let dgraphPayload;
    const utms = utm.getAll();

    const utmSource = (utms.hasOwnProperty('utm_source') && utm.utm_source) || '';
    const utmMedium = (utms.hasOwnProperty('utm_medium') && utm.utm_medium) || '';
    const utmCampaign = (utms.hasOwnProperty('utm_campaign') && utm.utm_campaign) || '';

    if (data.category === 'auto') {
      dgraphPayload = {
        visitorId: data.visitorId,
        utmSource: utmSource,
        utmMedium: utmMedium,
        utmCampaign: utmCampaign,
        category: data.category,
        leadInfo: {
          name: `${data.firstName} ${data.lastName}`,
          phoneNumber: data.phoneNumber,
          email: data.email,
          budget: data.budget,
          salary: 0,
          policyExpiryDate: data.policyExpiredDate,
          members: null,
          hasCompletedPSF: true,
          hasCreditCard: data.hasCreditCard == 'true',
          vehicleSpecification: {
            brand: data.brand,
            model: data.model,
            vehicleType: data.type,
            year: data.year,
            area: data.area,
            region: data.areaCode,
          },
        },
      };
    }

    if (data.category === 'health' || data.category === 'life') {
      dgraphPayload = {
        visitorId: data.visitorId,
        utmSource: utmSource,
        utmMedium: utmMedium,
        utmCampaign: utmCampaign,
        category: data.category,
        leadInfo: {
          name: `${data.firstName} ${data.lastName}`,
          phoneNumber: data.phoneNumber,
          email: data.email,
          policyExpiryDate: data.policyExpiredDate,
          members: [],
          hasCompletedPSF: false,
          hasCreditCard: true,
          dob: '',
          city: '',
        },
      };
    }

    if (dgraphPayload) {
      const dgraphRequest = {
        method: 'POST',
        body: JSON.stringify(dgraphPayload),
      };

      return HTTPClient(`${CORE_URL}/api/track/leads/`, dgraphRequest);
    }

    return null;
  };

  const sendToFBpixel = (data, leadID) => {
    // Facebook pixel
    const splitFullName = data.fullName.split(' ');

    const advancedMatching = {
      em: data.email,
      fn: splitFullName[0],
      ln: splitFullName[1],
      ph: data.phoneNumber,
      external_id: leadID,
      ge: data.gender,
      db: '', //date of birth
      ct: data.area,
      st: data.area,
      zp: data.areaCode,
      country: '',
    };

    const trackData = {
      event_id: leadID,
      content_category: `lead-submit-${data.brand}`,
      content_name: data.model,
    };

    import('react-facebook-pixel')
      .then((x) => x.default)
      .then((ReactPixel) => {
        ReactPixel.init(FB_PIXEL_ID, advancedMatching);
        if (data.category === 'auto') {
          ReactPixel.track('Lead', trackData);
        }
      });
  };

  const leadForm = getLeadForm((category_value ??= category));

  return (
    // Base Layout for all the lead-forms with discount banners as a configurable parameter
    <div
      className={`${styles['lifepal__lead--form']} ${styles[background]}`}
      style={{
        height: height,
        width: width,
      }}
    >
      {leadForm().component()}
      {showCallNotification && <CallNotification />}
    </div>
  );
};
